import { Box, Grid, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import {
  BasicTable,
  Breadcrumbs,
  CardComponent,
  CostTrendChart,
  DashedStandardDropdown,
  ExternalLink,
  HighCostClaimantsChart,
  OverviewCard,
  OverviewCardWithIcon,
  PageTitleAndSubTitle,
  Table,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import Loader from '../../_components/molecules/Loader';
import episodesOfCareMessages from '../../core-utils/messages/episodesOfCareMessages';
import { useNavigate } from 'react-router-dom';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { useStores } from '../../stores';
import { COMMON_TEXTS } from '../../core-utils/messages/CommonConstants';
import TabSelection from '../../_components/molecules/TabSelection';
import { valueWithText } from '@HomaHealth/ui-component-library/lib/_AIQ-components/molecules/OverviewCard';
import {
  getToolText,
  numFormatterWithSuffix,
  numberToCurrencyFormatter,
} from '../../core-utils/helper-utils/helper';
import DiseasePrevalenceTable from '../../_components/organisms/DiseasePrevalenceTable';
import {
  CostTrendData,
  CostbreakDownData,
  MemberClaimDetails,
  collapsibleMemberClaimsTableColumns,
  formatMemberDetails,
  formatTreatmentCostBreakdown,
  formatEpisodeValue,
  EpisodesValues,
  formatCostTrendData,
  formatCostBreakDownData,
} from '../../core-utils/helper-utils/episodesOfCareHelper';
import {
  formatMemberClaim,
  getTextWithToolTip,
} from '../../core-utils/helper-utils/riskPatientDetailsHelper';
import MedicalHistoryDetails from '../../_components/organisms/MedicalHistoryDetails';
import { EPISODES_OF_CARE, PATIENT_DETAILS } from '../../core-utils/routes';
import axios from 'axios';
import properties from '../../core-utils/properties';
import { Pagination } from '@mui/material';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import { observer } from 'mobx-react';
import RecommendationsTab from '../../_components/organisms/RecommendationsTab';
import { getRecommendationsData } from '../../core-utils/helper-utils/recommendationsTabHelper';
import { downloadPdf } from '../../core-utils/helper-utils/downloadPdfHelper';
import EditDurationModal from '../../_components/molecules/EditDurationModal';
import { getInfoIcon } from '../../core-utils/helper-utils/infoIconHelper';
import EmptyData from '../../_components/atoms/Icons/EmptyData';
import Snackbar from '../../_components/atoms/Snackbar';
import componentConstants from '../../core-utils/messages/componentConstants';
import { CircularLoader } from '@HomaHealth/ui-component-library';
import moment from 'moment';
import { getLast12MonthsRange } from '../CareQualityPage';
import {
  checkUserAccess,
  UserAccessConstants,
} from '../../core-utils/helper-utils/userAccess';
import CohortDetailsModal from '../../_components/organisms/CohortDetailsModal';
import summaryPageConstants from '../../core-utils/messages/summaryPageConstants';
import CohortFilterBar from '../../_components/molecules/CohortFilterBar';

export interface SummaryPageProps {}

export const costTrendProps = (
  preOperative: any,
  surgicalCost: any,
  postOperative: any,
  xAxisValues: any,
  quantile25: any,
  median: any,
  quantile75: any,
) => {
  return {
    showDBIcon: false,
    chartTitle: 'Cost Trend',
    chartSubTitle:
      'You can view and compare the cost of the episode selected here',
    showClickHere: false,
    datasetProp: [
      {
        seriesname: 'Pre Operative',
        plottooltext: '$displayValue',
        data: preOperative,
        color: patientAccessTheme.palette.warning[500],
      },
      {
        seriesname: 'Surgery Cost',
        plottooltext: '$displayValue',
        data: surgicalCost,
        color: patientAccessTheme.palette.primary[300],
      },
      {
        seriesname: 'Post Operative',
        plottooltext: '$displayValue',
        data: postOperative,
        color: patientAccessTheme.palette.status.success,
      },
    ],
    isTrendLine: true,
    quantile25: quantile25,
    median: median,
    quantile75: quantile75,
    xAxisTitle: '',
    yAxisTitle: ' ',
    sAxisTitle: ' ',
    spaceBetweenTitleAndChart: '54px',
    cardHeight: '420px',
    chartType: 'scrollstackedcolumn2d',
    xAxisValues: xAxisValues,
    customYAxisTitle: (
      <Grid style={{ position: 'absolute', top: '100px', left: '44px' }}>
        <Typography
          fontSize="12px"
          lineHeight="23px"
          fontType="semiBold"
          textColor={patientAccessTheme.palette.text.highemphasis}
        >
          Cost($)
        </Typography>
      </Grid>
    ),
  };
};

export const CostBreakdownChartProps = (
  preOperative: number,
  surgicalCost: number,
  postOperative: number,
  totalCost: number,
) => {
  return {
    data: [
      {
        value: preOperative,
        label: 'Pre Operative',
        color: patientAccessTheme.palette.warning[500],
        toolText: getToolText(preOperative),
      },
      {
        value: surgicalCost,
        label: 'Surgical Cost',
        color: patientAccessTheme.palette.primary[300],
        toolText: getToolText(surgicalCost),
      },
      {
        value: postOperative,
        label: 'Post Operative',
        color: patientAccessTheme.palette.status.success,
        toolText: getToolText(postOperative),
      },
    ],
    centerLabel: `$${numFormatterWithSuffix(totalCost)}`,
    centerLabelBold: true,
    centerLabelFontSize: '14',
    cardHeight: '420px',
    cardWidth: '376px',
    decimals: '2',
    title: 'Cost Breakdown',
    subtitle: 'This is average cost breakdown of the selected episode',
    clickHere: '',
    tooltip: '',
    showChartToolTip: '1',
    showSearchDB: false,
    formatNumberScale: '1',
    numberPrefix: '$',
  };
};

const EpisodesOfCareSelected: React.FC<SummaryPageProps> = observer(({}) => {
  const useStyles = makeStyles()({
    root: {
      display: 'flex',
      flexDirection: 'column',
      padding: '40px 0px 66px 63px',
    },
    duration: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      width: 'auto',
      height: '27px',
      background: patientAccessTheme.palette.status.white,
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      borderRadius: '4px',
      color: patientAccessTheme.palette.text.highemphasis,
      padding: '0px',
    },
  });

  //constants
  const navigate = useNavigate();
  const messages = episodesOfCareMessages;
  const episodeOfCareStore = useStores().episodeOfCareStore;
  const loginPageStore = useStores().loginPageStore;
  const cohortDetailsStore = useStores().cohortDetailsStore;
  const summaryCardsStore = useStores().summaryCardsStore;
  const { classes } = useStyles();
  const snackBarMessages = componentConstants.ORGANISMS.REPORTS_MODAL;
  const cohortMessages = summaryPageConstants.SUMMARY_PAGE;

  //states
  const [episode, setEpisode] = useState<string>('');

  const [costTrendData, setCostTrendData] = useState<CostTrendData>({
    preOperative: [],
    surgicalCost: [],
    postOperative: [],
    xAxisValues: [],
    quantile25: '',
    quantile75: '',
    median: '',
  });
  const [costBreakDownData, setCostBreakDownData] = useState<CostbreakDownData>(
    {
      preOperative: 0,
      surgicalCost: 0,
      postOperative: 0,
      totalCost: 0,
    },
  );
  const [qualityMeasures, setQualityMeasures] = useState<any>([]);

  const [pageSize] = useState<number>(5);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [memberClaimsDetails, setMemberClaimsDetails] = useState<
    MemberClaimDetails[]
  >([]);
  const [clickedMember, handleOnclickedMember] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [episodeDropDownValue, setEpisodeDropDownValue] = useState<string>(
    episode ?? '',
  );
  const [loader] = useState<any>(<Loader />);
  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [
    treatmentCostBreakdownData,
    setTreatmentCostBreakdownData,
  ] = useState<any>();

  const [episodesValues, setEpisodesValues] = useState<EpisodesValues>({
    episodeCount: '0',
    claimsCount: '0',
    opportunityCost: '$0.00',
    cohortCost: '$0.00',
  });

  //date selection code
  const employerGroupStore = useStores().employerGroupStore;
  const recommendedTabStore = useStores().recommendedTabStore;
  const memberSummaryStore = useStores().memberSummaryStore;
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const [areDatesReady, setAreDatesReady] = useState<boolean>(false);
  const [openCohortDetailsModal, setOpenCohortDetailsModal] = useState(false);

  //snacker bar variables and functions
  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });
  const handleCloseSnackBar = () => {
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };
  const handleCohortDetailsClose = () => {
    setOpenCohortDetailsModal(false);
  };

  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  const [recommendationLoading, setRecommendationLoading] = useState<boolean>(
    false,
  );
  const [recommendationFetched, setRecommendationFetched] = useState<boolean>(
    false,
  );
  const handleSelectedIndex = (index: number) => {
    setSelectedTabIndex(index);
    if (!recommendationFetched) {
      getRecommendationsData(
        employerGroupStore?.employerGroupResponseData?.eg_nid,
        messages.pageTitle.title,
        recommendedTabStore,
        setRecommendationLoading,
      );
      setRecommendationFetched(true);
    }
  };

  const handleDateChange = () => {
    setStartDate(employerGroupStore?.selectedDuration?.startDate);
    setEndDate(employerGroupStore?.selectedDuration?.endDate);
    setAreDatesReady((prev) => !prev);
  };

  //API calls
  const getOverviewCardData = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.episodesOfCare +
          properties.episodeTypeSummary,
        {
          max_claim_date:
            employerGroupStore.employerGroupResponseData.max_claim_date,
          eg_nid: employerGroupStore.employerGroupResponseData.eg_nid,
          episode_name: episode,
        },
      )
      .then((response) => {
        const data = response?.data?.data[0];
        const episodesValues = formatEpisodeValue(
          data?.claim_count,
          data?.episode_count,
          data?.eoc_opportunity_cost,
          data?.episode_cost,
        );
        setEpisodesValues(episodesValues);
      })
      .catch((ex) => {
        return ex;
      });
  };

  const getCostTrendChartData = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.episodesOfCare +
          properties.episodeMemberData,
        {
          max_claim_date:
            employerGroupStore.employerGroupResponseData.max_claim_date,
          eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
          episode_name: episode,
          pageNumber: 0,
          isMemberDetails: false,
        },
      )
      .then(async (res) => {
        if (res?.status === 200) {
          setCostTrendData(formatCostTrendData(res?.data?.data));
        }
      })
      .catch((error) => {
        return error;
      });
  };

  const getCostBreakDownChartData = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.episodesOfCare +
          properties.costBreakDownAPI,
        {
          max_claim_date:
            employerGroupStore.employerGroupResponseData.max_claim_date,
          eg_nid: employerGroupStore.employerGroupResponseData.eg_nid,
          episode_name: episode,
        },
      )
      .then(async (res) => {
        if (res?.status === 200) {
          setCostBreakDownData(formatCostBreakDownData(res?.data?.data));
        }
      })
      .catch((error) => {
        return error;
      });
  };

  const getTreatmentCostBreakdown = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.episodesOfCare +
          properties.treatmentCostBreakdown,
        {
          max_claim_date:
            employerGroupStore.employerGroupResponseData.max_claim_date,
          eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
          episode_name: episode,
        },
      )
      .then((res) => {
        if (res?.status === 200) {
          setTreatmentCostBreakdownData(
            formatTreatmentCostBreakdown(res?.data?.data),
          );
        }
      })
      .catch((error) => {
        return error;
      });
  };

  const getEpisodeMemberData = async () => {
    const max_claim_date =
      employerGroupStore.employerGroupResponseData.max_claim_date;
    setTableLoading(true);
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.episodesOfCare +
          properties.episodeMemberData,
        {
          max_claim_date: max_claim_date,
          eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
          episode_name: episode,
          isMemberDetails: true,
          pageNumber: pageNumber - 1,
        },
      )
      .then(async (res) => {
        if (res?.status === 200) {
          setTotalPages(
            res?.data?.data?.totalCount
              ? Math.ceil(Number(res?.data?.data?.totalCount) / pageSize)
              : 0,
          );
          setMemberClaimsDetails(
            await formatMemberDetails(
              res?.data?.data,
              moment(max_claim_date).subtract(1, 'year').format('YYYY-MM-DD'),
              max_claim_date,
              employerGroupStore?.employerGroupResponseData?.eg_nid,
              setQualityMeasures,
              episode,
            ),
          );
        }
      })
      .catch((error) => {
        return error;
      });
    setTableLoading(false);
  };

  //pagination functions
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value?: number,
  ) => {
    setPageNumber(value!);
  };

  //function to get all API calls
  const getAllCharts = async () => {
    setLoading(true);
    Boolean(startDate !== '' && endDate !== '')
      ? await Promise.all([
          getOverviewCardData(),
          getCostTrendChartData(),
          getCostBreakDownChartData(),
          getTreatmentCostBreakdown(),
        ]).then(() => {
          setLoading(false);
        })
      : '';
    setLoading(false);
  };

  //useEffects
  useEffect(() => {
    if (clickedMember) {
      memberSummaryStore.setPathDetails([
        [
          messages.MEMBER_PAGE_BREAD_CRUMBS.text,
          messages.MEMBER_PAGE_BREAD_CRUMBS.TITLE,
          {
            color: patientAccessTheme.palette.primary[500],
            fontWeight: 500,
          },
          () => {
            navigate(EPISODES_OF_CARE);
          },
        ],
        [
          messages.MEMBER_PAGE_BREAD_CRUMBS.text,
          messages.MEMBER_PAGE_BREAD_CRUMBS.MEMBER_CLAIMS,
          {
            color: patientAccessTheme.palette.text.highemphasis,
            fontWeight: 600,
          },
        ],
      ]);

      navigate(PATIENT_DETAILS, { state: { memberId: clickedMember } });
    }
  }, [clickedMember]);

  useEffect(() => {
    setEpisodeDropDownValue(episode);
    if (!episode) {
      return;
    }
    Boolean(startDate !== '' && endDate !== '') ? getAllCharts() : '';
  }, [areDatesReady, episodeDropDownValue, episode]);

  useEffect(() => {
    if (!episode) {
      return;
    }
    Boolean(startDate !== '' && endDate !== '') ? getEpisodeMemberData() : '';
  }, [pageNumber, areDatesReady, episode]);

  useEffect(() => {
    setEpisode(episodeOfCareStore.selectedEpisode);
    setStartDate(employerGroupStore?.selectedDuration?.startDate);
    setEndDate(employerGroupStore?.selectedDuration?.endDate);
    setAreDatesReady((prev) => !prev);
  }, [episodeOfCareStore.selectedEpisode]);
  const SummaryCards = () => {
    const useStyles = makeStyles()({
      root: {
        display: 'flex',
        flexDirection: 'column',
      },
      tableHeader: {
        height: '48px',
        backgroundColor: patientAccessTheme.palette.grey[100],
        borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        '&:first-child': {
          paddingLeft: '20px',
        },
        textAlign: 'left',
      },
      tableRow: {
        height: '56px',
        padding: '0px',
        borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        '&:first-child': {
          paddingLeft: '38px',
        },
      },
      headerText: {
        fontSize: '12px',
        fontHeight: '12px',
        fontWeight: 500,
        color: patientAccessTheme.palette.text.highemphasis,
      },
      tooltip: {
        backgroundColor: patientAccessTheme.palette.grey[900],
        padding: '10px',
        fontSize: '13px',
        fontFamily: 'Inter',
        fontWeight: 400,
        lineHeight: '16px',
        color: patientAccessTheme.palette.status.white,
        maxWidth: 365,
      },
      arrow: {
        color: patientAccessTheme.palette.grey[900],
        marginLeft: '50px',
      },
      popper: {
        marginTop: '-8px',
        marginLeft: '-34px',
      },
      emptyDataIconGrid: {
        marginTop: '20px',
        marginBottom: '20px',
      },
    });
    const { classes } = useStyles();

    const getMemberClaimsListData = () => {
      const memberClaimsTableData: any[] = [];

      memberClaimsDetails?.forEach(async (item) => {
        const eachMemberDetail: any = [];
        eachMemberDetail.push(item.member_unique_id);
        eachMemberDetail.push(numberToCurrencyFormatter(item.total_cost, true));
        eachMemberDetail.push(item.duration);
        eachMemberDetail.push(item.variance);
        eachMemberDetail.push(
          <MedicalHistoryDetails
            riskPatientDetails={formatMemberClaim(
              item.claimData.slice().reverse(),
              item.member_unique_id,
            )}
          />,
        );
        memberClaimsTableData.push(eachMemberDetail);
      });
      return memberClaimsTableData;
    };

    const getExternalLinkIcon = (
      title: string,
      onClick?: any,
      styles?: any,
    ) => {
      return (
        <Tooltip
          arrow
          classes={{
            tooltip: classes.tooltip,
            arrow: classes.arrow,
            popper: classes.popper,
          }}
          title={title}
          placement="bottom-start"
        >
          <Box style={{ display: 'flex', ...styles }} onClick={onClick}>
            <ExternalLink
              style={{
                width: '17px',
                height: '17px',
                marginLeft: '4px',
                fill: patientAccessTheme.palette.status.icon,
              }}
            />
          </Box>
        </Tooltip>
      );
    };

    // const getChipComponent = (
    //   chipType: 'Needs Intervention' | 'Good' | 'Average',
    //   label?: string | React.JSX.Element,
    // ) => {
    //   switch (chipType) {
    //     case 'Needs Intervention':
    //       return (
    //         <Chip
    //           label={
    //             <Typography
    //               fontSize="12px"
    //               lineHeight="15px"
    //               fontType="medium"
    //               textColor={patientAccessTheme.palette.AIQChips?.textColor1}
    //             >
    //               {label ?? 'Needs Intervention'}
    //             </Typography>
    //           }
    //           backgroundColor={patientAccessTheme.palette.AIQChips?.background1}
    //           border="0.3px solid rgba(226, 84, 84, 0.5)"
    //           borderRadius="4px"
    //           padding="5px 8px"
    //           height="25px"
    //           width={label ? '120px' : '150px'}
    //         />
    //       );
    //     case 'Good':
    //       return (
    //         <Chip
    //           label={
    //             <Typography
    //               fontSize="12px"
    //               lineHeight="15px"
    //               fontType="medium"
    //               textColor={patientAccessTheme.palette.AIQChips?.textColor2}
    //             >
    //               {label ?? 'Good'}
    //             </Typography>
    //           }
    //           backgroundColor={patientAccessTheme.palette.AIQChips?.background2}
    //           border="0.3px solid rgba(0, 130, 114, 0.5)"
    //           borderRadius="4px"
    //           padding="5px 8px"
    //           height="25px"
    //           width={label ? '120px' : '150px'}
    //         />
    //       );

    //     case 'Average':
    //       return (
    //         <Chip
    //           label={
    //             <Typography
    //               fontSize="12px"
    //               lineHeight="15px"
    //               fontType="medium"
    //               textColor={patientAccessTheme.palette.primary[500]}
    //             >
    //               {label ?? 'Average'}
    //             </Typography>
    //           }
    //           backgroundColor={
    //             patientAccessTheme.palette.AIQChips?.background15
    //           }
    //           border={'0.3px solid' + patientAccessTheme.palette.primary[500]}
    //           borderRadius="4px"
    //           padding="5px 8px"
    //           height="25px"
    //           width={label ? '120px' : '150px'}
    //         />
    //       );

    //     default:
    //       return <></>;
    //   }
    // };

    const measuresColumns = [
      {
        Header: (
          <Typography className={classes.headerText} style={{ width: '235px' }}>
            {'Quality Measure'}
          </Typography>
        ),
        Footer: 'MEASURES',
        accessor: 'measureDesc',
        sticky: 'left',
        Cell: ({ value, row }: any) => {
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                paddingRight: '20px',
                paddingLeft: '20px',
              }}
            >
              <div>{getTextWithToolTip(value, 30)}</div>
              {row?.original?.measureUrl &&
                row?.original?.measureUrl !== 'nan' && (
                  <div>
                    {getExternalLinkIcon(
                      'Click this icon to view an explanation about the measure',
                      () => {
                        window.open(row?.original?.measureUrl);
                      },
                      { cursor: 'pointer' },
                    )}
                  </div>
                )}
            </div>
          );
        },
      },
      {
        Header: (
          <Typography
            className={classes.headerText}
            style={{ width: '120px', display: 'flex' }}
          >
            {'Rate'}
            {getInfoIcon(
              'Percentage of compliant population out of eligible population for the measure',
            )}
          </Typography>
        ),
        Footer: 'RATE',
        accessor: 'rate',
        sticky: 'left',
        cellType: 'none',
        icon: 'none',
        Cell: ({ value }: any) => <div>{value}%</div>,
      },
      // {
      //   Header: (
      //     <Typography
      //       className={classes.headerText}
      //       style={{ width: '120px', display: 'flex' }}
      //     >
      //       {'Target Rate'}
      //       {getInfoIcon('National benchmark levels for the measure')}
      //     </Typography>
      //   ),
      //   Footer: 'Target Rate',
      //   accessor: 'nationalScore',
      //   sticky: 'left',
      //   cellType: 'none',
      //   icon: 'none',
      //   Cell: ({ value }: any) => <div>{value}%</div>,
      // },
      {
        Header: (
          <Typography className={classes.headerText} style={{ width: '120px' }}>
            {'Eligible'}
          </Typography>
        ),
        Footer: 'Eligible Members',
        accessor: 'eligible',
        sticky: 'left',
        cellType: 'none',
        icon: 'none',
      },
      {
        Header: (
          <Typography className={classes.headerText} style={{ width: '92px' }}>
            {'Compliant'}
          </Typography>
        ),
        Footer: 'Met Members',
        accessor: 'compliant',
      },
      {
        Header: (
          <Typography className={classes.headerText} style={{ width: '109px' }}>
            {'Non-Compliant'}
          </Typography>
        ),
        Footer: 'Unmet Members',
        accessor: 'nonCompliant',
        cellType: 'TextWithIcon',
        icon: 'info',
      },
      // {will be added later, getchipcomponent is also commented
      //   Header: (
      //     <Typography
      //       className={classes.headerText}
      //       style={{ width: '170px', display: 'flex' }}
      //     >
      //       {'Current Performance'}
      //       {getInfoIcon('As percentage of average national performance')}
      //     </Typography>
      //   ),
      //   Footer: 'Current Performance',
      //   accessor: 'measureScore',
      //   icon: 'none',
      //   Cell: ({ value, row }: any) => (
      //     <div>{getChipComponent(row?.original?.rank, value)}</div>
      //   ),
      // },
    ];

    return (
      <Grid container className={classes.root}>
        <Grid style={{ display: 'flex', marginBottom: '29px' }}>
          <Grid style={{ marginRight: '24px' }}>
            <OverviewCard
              title={messages.SUMMARY_CARDS.EPISODES}
              value={valueWithText(episodesValues.episodeCount, 'Episodes')}
              // showClickHere={true}
              rootStyles={{
                width: '176px',
                fontWeight: 600,
                fontSize: ' 24px',
              }}
            />
          </Grid>
          <Grid style={{ marginRight: '24px' }}>
            <OverviewCard
              title={messages.SUMMARY_CARDS.CLAIMS}
              value={episodesValues.claimsCount.toString()}
              // showClickHere={true}
              rootStyles={{
                width: '176px',
                fontWeight: 600,
                fontSize: ' 24px',
              }}
            />
          </Grid>
          <Grid style={{ marginRight: '24px' }}>
            <OverviewCard
              title={messages.SUMMARY_CARDS.COHORT_COST}
              value={episodesValues.cohortCost}
              // showClickHere={true}
              rootStyles={{
                width: '176px',
                fontWeight: 600,
                fontSize: ' 24px',
              }}
            />
          </Grid>
          <Grid style={{ marginRight: '24px' }}>
            <OverviewCardWithIcon
              title={messages.SUMMARY_CARDS.OPPORTUNITY_COST}
              values={[[episodesValues.opportunityCost, '']]}
              // percentValues={valueWithPercentage('0%', false)}
              rootStyles={{
                height: '100px',
              }}
              titleTypographyStyles={{
                fontSize: '14px',
                lineHeight: '24px',
                color: `${patientAccessTheme.palette.text.highemphasis}`,
                fontWeight: 600,
                margin: '0px 4px 0px 0px',
              }}
              toolTipContent={messages.SUMMARY_CARDS.OPPORTUNITY_COST_HOVER}
              toolTipStyles={{
                width: '222px',
              }}
            />
          </Grid>
        </Grid>
        <Grid style={{ display: 'flex', marginBottom: '28px' }}>
          <Grid style={{ marginRight: '24px' }}>
            <CostTrendChart
              {...costTrendProps(
                costTrendData.preOperative,
                costTrendData.surgicalCost,
                costTrendData.postOperative,
                costTrendData.xAxisValues,
                costTrendData.quantile25,
                costTrendData.median,
                costTrendData.quantile75,
              )}
            />
          </Grid>
          <HighCostClaimantsChart
            {...CostBreakdownChartProps(
              costBreakDownData.preOperative,
              costBreakDownData.surgicalCost,
              costBreakDownData.postOperative,
              costBreakDownData.totalCost,
            )}
          />
        </Grid>
        <Grid style={{ display: 'flex', marginBottom: '28px' }}>
          <CardComponent
            title={messages.COST_BREAK_DOWN.TITLE}
            subTitle={messages.COST_BREAK_DOWN.SUB_TITLE}
            body={
              <Grid style={{ marginTop: '14px' }}>
                <DiseasePrevalenceTable
                  data={treatmentCostBreakdownData}
                  handleEpisodeCategory={function () {}}
                  handleDiseaseCategory={function () {}}
                  isEpisodeOfCareSelected={true}
                  highestPrevalenceRate={0}
                />
              </Grid>
            }
            topRightItem={
              <></>
              //Un-comment when required.
              // <TextField
              //   placeholder={'Search'}
              //   height={'40px'}
              //   width={'400px'}
              //   showStartAdornment={true}
              // />
            }
            width="1178px"
            height="fit-content"
          />
        </Grid>
        {false && (
          <Grid style={{ display: 'flex', marginBottom: '28px' }}>
            <CardComponent
              title={messages.CARE_QUALITY.TITLE}
              subTitle={messages.CARE_QUALITY.SUB_TITLE}
              body={
                <Grid style={{ marginTop: '14px' }}>
                  {qualityMeasures.length > 0 ? (
                    <Table
                      classNameForTableHeaderRowTop={classes.tableHeader}
                      classNameForTableDataRow={classes.tableRow}
                      classNameForTableHeader={classes.tableHeader}
                      columns={measuresColumns}
                      data={qualityMeasures}
                      onHoverRowData={() => {}}
                      controlledPageIndex={0}
                      isManualPagination={true}
                    />
                  ) : (
                    <Grid
                      container
                      sx={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                      }}
                    >
                      <Grid className={classes.emptyDataIconGrid}>
                        <EmptyData />
                      </Grid>
                      <Grid>
                        <Typography
                          fontSize="14px"
                          fontType="regular"
                          lineHeight="24.78px"
                          textColor={
                            patientAccessTheme.palette.text.mediumemphasis
                          }
                        >
                          {messages.CARE_QUALITY.EMPTY_DATA}
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              }
              width="1178px"
              height="fit-content"
            />
          </Grid>
        )}
        <Grid style={{ display: 'flex', marginBottom: '28px' }}>
          <CardComponent
            title={messages.MEMBER_CLAIMS_TABLE.TITLE}
            subTitle={messages.MEMBER_CLAIMS_TABLE.SUB_TITLE}
            body={
              tableLoading ? (
                <CircularLoader />
              ) : (
                <Grid style={{ marginTop: '28px' }}>
                  <BasicTable
                    displayType="row"
                    columns={collapsibleMemberClaimsTableColumns}
                    rowsData={getMemberClaimsListData()}
                    isCollapsiblePatientTable={true}
                    width="1127px"
                    getMemberId={(value: string) => {
                      handleOnclickedMember(value);
                    }}
                    headerCellHeight="48px"
                    showPagination={false}
                  />
                  {totalPages > 0 && (
                    <Grid
                      style={{
                        position: 'absolute',
                        right: '24px',
                        bottom: '16px',
                      }}
                    >
                      <Pagination
                        count={totalPages}
                        page={pageNumber}
                        onChange={handlePageChange}
                        color="primary"
                        size="small"
                      />
                    </Grid>
                  )}
                </Grid>
              )
            }
            topRightItem={
              <></>
              //Uncomment When required
              // <TextField
              //   placeholder={'Search'}
              //   height={'40px'}
              //   width={'400px'}
              //   showStartAdornment={true}
              // />
            }
            width="1178px"
            height="fit-content"
            padding="20px 24px 64px 20px"
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      {loading ? (
        loader
      ) : (
        <Grid container className={classes.root}>
          <Grid
            style={{
              marginBottom: '36px',
              width: '1182px',
            }}
          >
            <Grid style={{ marginBottom: '19px' }}>
              <Breadcrumbs
                itemList={[
                  [
                    'text',
                    'Episodes Of Care',
                    {
                      color: patientAccessTheme.palette.primary[500],
                      fontWeight: 500,
                    },
                    () => {
                      navigate('/episodes-of-care');
                    },
                  ],
                  [
                    'text',
                    `${episode}`,
                    {
                      color: patientAccessTheme.palette.text.highemphasis,
                      fontWeight: 600,
                    },
                  ],
                ]}
              />
            </Grid>
            <Grid style={{ position: 'relative' }}>
              <PageTitleAndSubTitle
                title={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        fontSize="18px"
                        lineHeight="22px"
                        fontType="semiBold"
                        textColor={patientAccessTheme.palette.text.highemphasis}
                        margin="0px 8px 0px 0px"
                      >
                        {messages.EPISODE_SELECTED.TITLE}
                      </Typography>
                      <DashedStandardDropdown
                        options={episodeOfCareStore?.episodeDropDownList}
                        displayValue={episodeOfCareStore?.episodeDropDownList}
                        value={episodeDropDownValue}
                        handleChange={(e: any) => {
                          const selectedEpisode = e.target.value ?? '';
                          // recommendedTabStore?.setCategoryToBeCalled(
                          //   `/disease-prelavence/${selectedEpisode}`,
                          // );
                          setEpisode(selectedEpisode);
                          setPageNumber(1);
                          setEpisodeDropDownValue(selectedEpisode);
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: '8px',
                      }}
                    >
                      <Typography
                        fontSize="17px"
                        lineHeight="21px"
                        textColor={
                          patientAccessTheme.palette.text.mediumemphasis
                        }
                        fontType="semiBold"
                        margin="0px 8px"
                      >
                        {COMMON_TEXTS.FOR}
                      </Typography>
                      <Grid className={classes.duration}>
                        {getLast12MonthsRange(
                          employerGroupStore.employerGroupResponseData
                            .max_claim_date,
                        )}
                      </Grid>
                      <Grid style={{ display: 'none' }}>
                        <EditDurationModal
                          isYearGrid={false}
                          handleDateChange={handleDateChange}
                        />
                      </Grid>
                    </div>
                  </div>
                }
                subTitle={messages.EPISODE_SELECTED.SUB_TITLE}
                showDownloadButton={true}
                downloadButtonAction={() => {
                  downloadPdf(
                    'individualEpisodeOfCareReport',
                    {
                      startDate,
                      endDate,
                      eg_nid_list:
                        employerGroupStore?.employerGroupResponseData?.eg_nid,
                      employer:
                        employerGroupStore?.employerGroupResponseData
                          ?.employer_group_name,
                      service_year: moment(startDate).year().toString(),
                      episode_name: episode,
                      // member_unique_ids: [memberClaimsDetails?.member_unique_id],
                      max_claim_date:
                        employerGroupStore?.employerGroupResponseData
                          ?.max_claim_date,
                    },
                    handleOpenSnackBar,
                    'open',
                  );
                  handleOpenSnackBar(
                    snackBarMessages.SNACKBAR_MESSAGES.OPEN_REPORT,
                    'info',
                    6000,
                  );
                }}
                showQueryDataButton={checkUserAccess(
                  UserAccessConstants.COHORT_DETAILS,
                  loginPageStore.userResponse.role.name,
                  loginPageStore.userFeatures,
                )}
                queryButtonText={
                  cohortDetailsStore.cohortDetailsList?.length > 0 &&
                  cohortDetailsStore.cohortDetailsList[0].category !== ''
                    ? 'Edit Cohort'
                    : 'Add Cohort'
                }
                queryDataAction={() => {
                  if (
                    checkUserAccess(
                      UserAccessConstants.COHORT_DETAILS,
                      loginPageStore.userResponse.role.name,
                      loginPageStore.userFeatures,
                    )
                  ) {
                    setOpenCohortDetailsModal(true);
                  } else {
                    // setOpenFeatureAccessModal(true);
                  }
                }}
              />
            </Grid>
            <CohortFilterBar />
            <TabSelection
              selectedTabIndex={selectedTabIndex}
              setSelectedTabIndex={handleSelectedIndex}
              recommendationFetched={recommendationFetched}
            />
          </Grid>

          <Grid>
            {selectedTabIndex === 0 && <SummaryCards />}
            {selectedTabIndex === 1 && (
              <RecommendationsTab
                recommendationLoading={recommendationLoading}
                isInternalPage={true}
              />
            )}
          </Grid>
        </Grid>
      )}
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
      <CohortDetailsModal
        open={openCohortDetailsModal}
        isPage={true}
        setOpenCohortDetailsModal={setOpenCohortDetailsModal}
        handleClose={handleCohortDetailsClose}
        title={cohortMessages.COHORT_DETAILS.TITLE}
        subTitle={cohortMessages.COHORT_DETAILS.SUBTITLE}
        totalResults={summaryCardsStore.totalEnrolled?.totalCount}
        // startDate={
        //   aggregationParameter === 'paid' ? paidStartDate : incurredStartDate
        // }
        // endDate={
        //   aggregationParameter === 'paid' ? paidEndDate : incurredEndDate
        // }
        employerGroup={employerGroupStore?.employerGroupResponseData?.eg_nid}
        type="data"
      />
    </>
  );
});

export default EpisodesOfCareSelected;
